import { EditorContent } from "@tiptap/react"
import classNames from "classnames"
import { cloneElement, forwardRef, useImperativeHandle } from "react"
import { FORM_COMPONENT_FOCUS_CLASSES, FOCUS_STATE_BASE_CLASSES } from "../utils/focusStateClasses"
import { MenuBar } from "./MenuBar"
import { RichTextEditorProps } from "./types"
import { useEditor } from "./useEditor"
import { twMerge } from "tailwind-merge"
import {
  BASE_CLASSES,
  BASE_DISABLED_CLASSES,
  BASE_ERROR_CLASSES,
  BASE_FOCUS_CLASSES,
} from "./constants"
import { isFormLabelComponent } from "../utils/isFormLabelComponent"
import { WRAPPER_CLASSES, WRAPPER_MD_CLASSES } from "../utils/formComponentWrapperClasses"

export type RichTextEditorRef = {
  setContent: (content: string) => void
}

export const RichTextEditor = forwardRef<RichTextEditorRef, RichTextEditorProps>(
  (
    {
      className = "",
      disabled = false,
      defaultValue = "",
      editorClassName = "",
      error = undefined,
      id = "rte",
      isFixedHeight = false,
      label = null,
      name = undefined,
      onBlur = undefined,
      onChange = undefined,
      onFocus = undefined,
      placeholder = undefined,
    },
    ref,
  ) => {
    const editor = useEditor({
      disabled,
      defaultValue,
      editorClassName,
      error,
      ariaLabelledBy: id,
      isFixedHeight,
      onBlur,
      onChange,
      onFocus,
      placeholder,
    })

    // automatically apply id to label
    const _label = isFormLabelComponent(label)
      ? cloneElement(label, {
          id: id as string,
          ...label.props,
        })
      : label

    const _className = classNames(WRAPPER_CLASSES, WRAPPER_MD_CLASSES, className)

    useImperativeHandle(ref, () => {
      return {
        setContent: content => {
          editor?.commands.setContent(content)
        },
      }
    })

    return (
      <div className={_className}>
        {_label}
        <div
          className={twMerge(
            classNames(BASE_CLASSES, FOCUS_STATE_BASE_CLASSES, FORM_COMPONENT_FOCUS_CLASSES, {
              [BASE_ERROR_CLASSES]: error,
              [BASE_FOCUS_CLASSES]: !error,
              [BASE_DISABLED_CLASSES]: disabled,
            }),
          )}
        >
          <MenuBar editor={editor} disabled={disabled} />
          <EditorContent
            data-testid={`rich-text-editor-${name}`}
            id={id}
            name={name}
            editor={editor}
          />
        </div>
        {!!error && <span className="mt-2 text-sm text-error-base">{error}</span>}
      </div>
    )
  },
)

RichTextEditor.displayName = "RichTextEditor"
